/* oxlint-disable react/refs -- react-dnd connects to DOM nodes through refs */
import type { Identifier, XYCoord } from 'dnd-core'
import { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'

type DragItem = { id: string; index: number }

/**
 * One row of a vertical list that reorders by dragging a handle. The row moves as soon as the
 * pointer passes the middle of its neighbour, the same feel as `DraggableItem`, but the caller
 * owns all markup: attach `rowRef` to the row and `handleRef` to the grip, so text inside the
 * row stays selectable.
 */
export function useSortableItem({
  type,
  id,
  index,
  onMove,
}: {
  type: string
  id: string
  index: number
  onMove: (from: number, to: number) => void
}) {
  const rowRef = useRef<HTMLDivElement>(null)

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: type,
    collect: (monitor) => ({ handlerId: monitor.getHandlerId() }),
    hover: (item, monitor) => {
      if (!rowRef.current || item.index === index) return
      const rect = rowRef.current.getBoundingClientRect()
      const middle = (rect.bottom - rect.top) / 2
      const pointer = (monitor.getClientOffset() as XYCoord).y - rect.top
      if (item.index < index && pointer < middle) return
      if (item.index > index && pointer > middle) return
      onMove(item.index, index)
      // react-dnd's own examples mutate the dragged item so the next hover compares against
      // where it now sits rather than where it started.
      item.index = index
    },
  })

  const [{ isDragging }, handleRef, preview] = useDrag({
    type,
    item: () => ({ id, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  })

  preview(drop(rowRef))
  return { rowRef, handleRef, isDragging, handlerId }
}
